import { useCallback, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { isPast } from 'date-fns';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { selectAccessPinLockedTime } from 'store/user/selectors';

import { RetryController } from 'services/opf-retry-mechanism';
import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import changePin from 'screens/Settings/ParentalControl/utils/changePin';
import useUpdateCachedPins from 'screens/Settings/hooks/useUpdateCachedPins.ts';

import { AppNotification } from 'components/Notification/types';
import { PinDialogRef } from 'components/PinDialog';
import { PinDialogStatus } from 'components/PinDialog/hooks/getPinDialogText';
import usePinDialogStatus from 'components/PinDialog/hooks/usePinDialogStatus';

const { SUCCESSFUL_PIN_CHANGE } = LANGUAGE_KEYS;

function useChangePin() {
    const { t } = useTranslation();
    const pinDialogRef = useRef<null | PinDialogRef>(null);

    const accessPinLockedTime = useSelector(selectAccessPinLockedTime);

    const [changePinDialogVisible, setChangePinDialogVisible] = useState(false);
    const [pinChangedNotification, setPinChangedNotification] = useState<
        AppNotification | undefined
    >(undefined);
    const [pinChangeFailure, setPinChangeFailure] = useState(false);
    const [currentPin, setCurrentPin] = useState('');
    const [newPin, setNewPin] = useState('');

    const onUpdateCachedPins = useUpdateCachedPins();

    const handleResetPinChangeNotification = useCallback(() => {
        setPinChangedNotification(undefined);
    }, []);

    const handleResetPinChangeFailure = useCallback(() => {
        setPinChangeFailure(false);
    }, []);

    const handleClearPin = useCallback(() => {
        if (pinDialogRef.current) {
            pinDialogRef.current?.clear();
        }
    }, []);

    const handleCloseChangePinDialog = useCallback(() => {
        setChangePinDialogVisible(false);
        setCurrentPin('');
        setNewPin('');

        handleClearPin();
    }, [handleClearPin]);

    const { pinDialogStatus, setPinDialogStatus, onValidatePin } =
        usePinDialogStatus(pinDialogRef, undefined, handleCloseChangePinDialog);

    const handleShowChangePinDialog = useCallback(() => {
        setPinDialogStatus(
            accessPinLockedTime && !isPast(accessPinLockedTime)
                ? PinDialogStatus.BLOCKED
                : PinDialogStatus.ENTER_PIN,
        );

        setChangePinDialogVisible(true);
    }, [accessPinLockedTime, setPinDialogStatus]);

    const handleChangePin = useCallback(async () => {
        const retryChangePinAction = async () => {
            await changePin(currentPin, newPin);
            handleCloseChangePinDialog();
            onUpdateCachedPins();

            setPinChangedNotification({
                title: t(SUCCESSFUL_PIN_CHANGE),
            });
        };

        try {
            await retryChangePinAction();
        } catch (error: any) {
            if (isErrorNeedsRetry(error)) {
                const handleClose = () => {
                    RetryController.hideVitalDialog();
                    handleCloseChangePinDialog();
                };

                RetryController.showVitalDialog({
                    actionButtonTitle: LANGUAGE_KEYS.CLOSE_BUTTON,
                    actionButtonOnPress: handleClose,
                    vitalDialogOnBackPress: handleClose,
                });
                RetryController.registerRetryCallback(retryChangePinAction, {
                    retryDuration: 10 * 60 * 1000,
                    maxRetryCount: 10,
                });
                RetryController.executeRetry();
            } else {
                setPinChangeFailure(true);
            }
        }
    }, [currentPin, handleCloseChangePinDialog, newPin, onUpdateCachedPins, t]);

    const handleNextPinEntered = useCallback(
        async (pin: string) => {
            if (
                pinDialogStatus === PinDialogStatus.ENTER_PIN ||
                pinDialogStatus === PinDialogStatus.INCORRECT
            ) {
                onValidatePin({
                    pin,
                    onPinValid: () => {
                        setCurrentPin(pin);
                        setPinDialogStatus(PinDialogStatus.NEW_PIN);
                    },
                });
            } else if (
                pinDialogStatus === PinDialogStatus.NEW_PIN ||
                pinDialogStatus === PinDialogStatus.NEW_PIN_DOESNT_MATCH
            ) {
                if (pin === newPin) {
                    handleChangePin();
                } else if (!newPin) {
                    setNewPin(pin);
                    setTimeout(() => {
                        setPinDialogStatus(PinDialogStatus.CONFIRM_NEW_PIN);
                    }, 150);
                } else if (pin && newPin && pin !== newPin) {
                    setTimeout(() => {
                        setPinDialogStatus(
                            PinDialogStatus.NEW_PIN_DOESNT_MATCH,
                        );
                    }, 150);
                }
                setTimeout(() => {
                    handleClearPin();
                }, 150);
            } else if (pinDialogStatus === PinDialogStatus.CONFIRM_NEW_PIN) {
                if (pin === newPin) {
                    handleChangePin();
                } else {
                    setPinDialogStatus(PinDialogStatus.NEW_PIN_DOESNT_MATCH);
                    handleClearPin();
                }
            }
        },
        [
            pinDialogStatus,
            onValidatePin,
            setPinDialogStatus,
            newPin,
            handleChangePin,
            handleClearPin,
        ],
    );

    return {
        pinDialogRef,
        changePinDialogVisible,
        changePinDialogStatus: pinDialogStatus,
        pinChangedNotification,
        pinChangeFailure,
        onShowChangePinDialog: handleShowChangePinDialog,
        onCloseChangePinDialog: handleCloseChangePinDialog,
        onNextPinEntered: handleNextPinEntered,
        onResetPinChangeNotification: handleResetPinChangeNotification,
        onResetPinChangeFailure: handleResetPinChangeFailure,
    };
}

export default useChangePin;
